<template>
  <w-flex class="form-layout" align="stretch">
    <form-aside-mobile
      v-if="resolvedBreakpoint.sm"
      :tabs="tabs"
      :current-tab-name="selectedTab.name"
      :project-name="projectName"
      :document-title="documentTitle"
      :version="version"
      :is-open="isMobileOpen"
      @change-tab="
        (tab) => {
          setTab(tab);
          isMobileOpen = false;
        }
      "
      @save="onSave"
      @cancel="onCancel"
      @close="isMobileOpen = false"
    />

    <form-aside
      v-else
      :tabs="tabs"
      :current-tab-name="selectedTab.name"
      :project-name="projectName"
      :document-title="documentTitle"
      :version="version"
      @change-tab="setTab"
    />

    <w-flex fluid direction="col" class="form-layout__body" align="stretch">
      <breadcrumbs-bar :breadcrumb-links="breadcrumbLinks" />

      <w-flex fluid class="form-layout__content w-scroll">
        <tab-container
          :active-tab="selectedTab"
          :tabs="tabs"
          :is-last-index="isLastIndex"
          :is-first-index="isFirstIndex"
          :number-of-steps="tabs.length"
          @fields-changed="setCurrentTabFields($event)"
          @next-step="nextTab"
          @prev-step="prevTab"
          @save="onSave"
        >
          <template v-for="slot in slots" #[slot]>
            <slot :name="slot">
              <div>No content provided for slot: {{ slot }}</div>
            </slot>
          </template>
        </tab-container>
      </w-flex>

      <actions-bar-mobile
        v-if="resolvedBreakpoint.sm"
        :is-super-admin="isSuperAdmin"
        :need-autosave="needAutosave"
        :is-last-tab="isLastIndex"
        @open-navigation="isMobileOpen = true"
        @save="onSave"
        @goNext="nextTab"
        @cancel="onCancel"
        @validate-all="onValidateAll"
      />

      <actions-bar
        v-else
        :is-super-admin="isSuperAdmin"
        :need-autosave="needAutosave"
        @save="onSave"
        @cancel="onCancel"
        @validate-all="onValidateAll"
      />
    </w-flex>
  </w-flex>

  <w-toast-container position="top-right" :toasts="resolvedToastService?.list?.value ?? []" />
</template>

<script setup lang="ts">
import { toastService } from '@witkac/vue3-components';
import { breakpoint } from '@witkac/vue3-components';
import { onMounted, useSlots, ref } from 'vue';

import { Theme } from '@/enums/theme.enum';
import { confirmService } from '@/services/confirm.service';
import type { BreadcrumbItem } from '@/typings/breadcrumbItem';
import type { ConfirmOptions } from '@/typings/confirmOptions';
import type { Tab } from '@/typings/tab';
import type { ComponentModel } from '@/typings/types';

import ActionsBar from '../layoutComponents/ActionsBar.vue';
import ActionsBarMobile from '../layoutComponents/ActionsBarMobile.vue';
import BreadcrumbsBar from '../layoutComponents/BreadcrumbsBar.vue';
import FormAside from '../layoutComponents/FormAside.vue';
import FormAsideMobile from '../layoutComponents/FormAsideMobile.vue';
import TabContainer from '../tab/TabContainer.vue';

defineProps<{
  breadcrumbLinks: Array<BreadcrumbItem>;
  version?: string;
  projectName?: string;
  documentTitle?: string;
  needAutosave?: boolean;
  isFirstIndex: boolean;
  isLastIndex: boolean;
  isSuperAdmin?: boolean;
  tabs: Tab[];
  selectedTab: Tab;
  setCurrentTabFields: (fields: ComponentModel[]) => void;
  prevTab: () => any;
  nextTab: () => any;
  setTab: (Tab: Tab) => any;
  onSave: (leave: boolean) => any;
  onCancel: () => any;
  onValidateAll: () => any;
}>();

const currentTheme = Theme.Light;

const resolvedToastService: typeof toastService =
  (globalThis as any)?.vue3Components?.toastService ?? toastService;
const resolvedBreakpoint: typeof breakpoint =
  (globalThis as any)?.vue3Components?.breakpoint ?? breakpoint;

const isMobileOpen = ref(false);

onMounted(() => {
  document.body.classList.add(`w-theme--${currentTheme}`);
});

const slots: any = Object.keys(useSlots());

defineExpose({
  confirmService: {
    configurable: (message: string, options?: ConfirmOptions | undefined) =>
      confirmService.configurable(message, options),
  },
});
</script>

<style lang="scss">
@font-face {
  font-family: 'Open Sans', sans-serif;
  src: url('/fonts/OpenSans.ttf') format('ttf');
}

body {
  font-family: 'Open Sans', sans-serif;
  font-weight: 350;
  letter-spacing: -0.01em;
  line-height: 1.45;
  padding: 0;
  margin: 0;
}

button,
a,
input {
  font-family: inherit;
}

.form-layout {
  height: 100vh;

  &__content {
    background: w.color('background');
    height: 0;
    overflow-y: auto;
    overflow-x: hidden;
  }

  &__body {
    width: 0;
    flex: 1;
  }
}

.sb-show-main.sb-main-padded {
  padding: 0;
}
</style>
